import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Button, Field, Select, Badge } from "@/lib/ui";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtDT = (d) => (d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export default function Referrals() {
  const [sellers, setSellers] = useState([]);
  const [log, setLog] = useState([]);
  const [referrer, setReferrer] = useState("");
  const [referee, setReferee] = useState("");
  const [beneficiary, setBeneficiary] = useState("referrer");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/sellers").then(({ data }) => setSellers(data)).catch(() => {});
    api.get("/admin/referral-bonuses").then(({ data }) => setLog(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const byId = useMemo(() => Object.fromEntries(sellers.map((s) => [s.id, s])), [sellers]);
  const isActive = (s) => !!(s && s.subscription && s.subscription.status === "active" && s.subscription.expiry);
  const opt = (s) => `${s.company_name || "(no company)"} · ${s.phone}${isActive(s) ? "" : " · no active plan"}`;

  const refereeSeller = byId[referee];
  const refExpiry = isActive(refereeSeller) ? refereeSeller.subscription.expiry : null;
  const beneficiarySeller = beneficiary === "referrer" ? byId[referrer] : byId[referee];
  const benExpiry = isActive(beneficiarySeller) ? beneficiarySeller.subscription.expiry : null;

  const apply = async () => {
    if (!referrer) { toast.error("Select the referrer"); return; }
    if (!referee) { toast.error("Select the referee (mandatory)"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/referral-bonus", { referrer_id: referrer, referee_id: referee, beneficiary });
      toast.success(`Free month added. New expiry: ${fmt(data.new_expiry)}`);
      setReferrer(""); setReferee(""); setBeneficiary("referrer");
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="referrals-page">
      <h1 className="font-head text-2xl font-extrabold text-slate-900">Referral Bonus</h1>
      <p className="mt-1 text-sm text-slate-500">Reward a referral with 1 free month (+30 days). Pick the referrer and the referee, choose who the bonus goes to, and click <b>Add Free Month</b> — the new expiry date is calculated automatically.</p>

      <div className="mt-5 max-w-2xl rounded-2xl border border-slate-200 bg-white p-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Referrer" required>
            <Select data-testid="referral-referrer-select" value={referrer} onChange={(e) => setReferrer(e.target.value)}>
              <option value="">Select referrer…</option>
              {sellers.map((s) => <option key={s.id} value={s.id}>{opt(s)}</option>)}
            </Select>
          </Field>
          <Field label="Referee (new joinee)" required>
            <Select data-testid="referral-referee-select" value={referee} onChange={(e) => setReferee(e.target.value)}>
              <option value="">Select referee…</option>
              {sellers.map((s) => <option key={s.id} value={s.id}>{opt(s)}</option>)}
            </Select>
          </Field>
        </div>

        <div className="mt-4">
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Who gets the free month?</p>
          <div className="flex gap-2">
            {[{ k: "referrer", t: "Referrer Bonus" }, { k: "joinee", t: "New Joinee Bonus" }].map((b) => (
              <button key={b.k} type="button" data-testid={`referral-beneficiary-${b.k}`}
                onClick={() => setBeneficiary(b.k)}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-semibold transition ${beneficiary === b.k ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"}`}>
                {b.t}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="referral-preview">
          {referee && !refExpiry && (
            <p className="text-red-600"><i className="fa-solid fa-circle-exclamation mr-1" />The referee has not purchased a package yet — bonus cannot be applied.</p>
          )}
          {beneficiarySeller && !benExpiry && (
            <p className="text-red-600"><i className="fa-solid fa-circle-exclamation mr-1" />The {beneficiary === "referrer" ? "referrer" : "new joinee"} has no active package to extend — no bonus.</p>
          )}
          {benExpiry && refExpiry && (
            <p className="text-slate-700">
              <i className="fa-solid fa-calendar-plus mr-1 text-emerald-600" />
              Current expiry <b>{fmt(benExpiry)}</b> + 30 days = new expiry <b className="text-emerald-700">{fmt(new Date(new Date(benExpiry).getTime() + 30 * 86400000))}</b>
            </p>
          )}
          {!referee && <p className="text-slate-400">Select a referrer and referee to preview the new expiry date.</p>}
        </div>

        <Button data-testid="referral-apply-btn" className="mt-4 w-full" onClick={apply} disabled={saving}>
          <i className="fa-solid fa-gift" /> {saving ? "Applying…" : "Add Free Month"}
        </Button>
      </div>

      <h3 className="mt-8 font-head text-lg font-bold text-slate-900">Referral Reward Log</h3>
      <p className="text-sm text-slate-500">Audit trail of every free-month reward applied.</p>
      {log.length === 0 ? (
        <div className="mt-3 rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-400" data-testid="referral-log-empty">
          <i className="fa-solid fa-receipt mb-2 block text-2xl" />No referral rewards applied yet.
        </div>
      ) : (
        <div className="mt-3 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="min-w-full text-sm" data-testid="referral-log-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-4 py-3">Referrer</th>
                <th className="px-4 py-3">Referee</th>
                <th className="px-4 py-3">Referee's Package</th>
                <th className="px-4 py-3">Bonus To</th>
                <th className="px-4 py-3">Prev. Expiry</th>
                <th className="px-4 py-3">Added</th>
                <th className="px-4 py-3">New Expiry</th>
                <th className="px-4 py-3">Applied On</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {log.map((r) => (
                <tr key={r.id} data-testid={`referral-log-row-${r.id}`}>
                  <td className="px-4 py-3"><div className="font-semibold text-slate-800">{r.referrer_company || "—"}</div><div className="text-xs text-slate-400">{r.referrer_phone}</div></td>
                  <td className="px-4 py-3"><div className="font-semibold text-slate-800">{r.referee_company || "—"}</div><div className="text-xs text-slate-400">{r.referee_phone}</div></td>
                  <td className="px-4 py-3 text-slate-700">{r.referee_package || "—"}</td>
                  <td className="px-4 py-3"><Badge tone={r.beneficiary === "referrer" ? "orange" : "green"}>{r.beneficiary === "referrer" ? "Referrer" : "New Joinee"}</Badge></td>
                  <td className="px-4 py-3 text-slate-600">{fmt(r.previous_expiry)}</td>
                  <td className="px-4 py-3 font-semibold text-emerald-600">+{r.extension_days}d</td>
                  <td className="px-4 py-3 font-semibold text-slate-800">{fmt(r.new_expiry)}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{fmtDT(r.applied_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
